import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Button } from "@/shared/components/ui/button";
import { Check, ListFilter } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import { cn } from "@/shared/lib/utils";
import t from "@/lang/lang";
import { Filter } from "@/shared/lib/filter/filter";
import { ServiceKind } from "@bitory-gmbh/bsm";
import { useServiceKindList } from "@/services/backend/services/hooks/use-service-kind-list";
import { useOmniProcessAvailable } from "@/services/backend/services/hooks/use-omni-process-available";

export interface ServiceKindFilterProps {
  filter: Filter<ServiceKind> | undefined;
  onFilterChange: (filter: Filter<ServiceKind>) => void;
  hidden?: boolean;
  /** kinds limits the options to these kinds; all kinds by default. */
  kinds?: ServiceKind[];
  /** omni offers the omni processes, those without a service kind. */
  omni?: boolean;
}

export function ServiceKindFilter({
  filter,
  onFilterChange,
  hidden = false,
  kinds,
  omni = false,
}: ServiceKindFilterProps) {
  // This allows us to accept undefined filters,
  // which is common in list requests. This makes
  // it nicer to call the component.
  const filterVal = filter ?? { active: false, values: [] };
  // simplifies some code below
  const currentValues = filterVal.values ?? [];

  const serviceKindList = useServiceKindList().filter(
    (kind) => !kinds || kinds.includes(kind),
  );
  const omniAvailable = useOmniProcessAvailable();
  // The empty service kind is the one of omni processes.
  const options: { value: ServiceKind; label: string }[] = [
    ...(omni && omniAvailable
      ? [{ value: "", label: t("Omni (ohne Leistungsart)") }]
      : []),
    ...serviceKindList.map((serviceKind) => ({
      value: serviceKind,
      label: serviceKind,
    })),
  ];

  if (hidden) {
    return null;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <ListFilter />
          {t("Dienstleistungen")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start">
        <Command>
          <CommandInput placeholder={t("Dienstleistung suchen")} />
          <CommandList>
            <CommandEmpty className="py-3 text-center text-sm">
              {t("Dienstleistung nicht gefunden")}
            </CommandEmpty>
            <CommandGroup>
              {options.map(({ value: serviceKind, label }) => (
                <CommandItem
                  key={label}
                  value={label}
                  onSelect={() => {
                    const values = currentValues.includes(serviceKind)
                      ? currentValues.filter((v) => v !== serviceKind)
                      : [...currentValues, serviceKind];
                    onFilterChange({
                      active: values.length > 0,
                      values,
                    });
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      currentValues.includes(serviceKind)
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                  {label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
